import { Message, Provider, ProviderInit, ProviderResponse, FunctionDefinition } from "./types";
import { MetisError } from "../errors/MetisError";

// Groq API Client (OpenAI-compatible)
interface GroqMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string | null;
  tool_calls?: any[];
  tool_call_id?: string;
  name?: string;
}

interface GroqTool {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: any;
  };
}

interface GroqResponse {
  choices: {
    message: {
      role: string;
      content: string | null;
      tool_calls?: any[];
    };
  }[];
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export class GroqProvider implements Provider {
  public readonly name = "groq";
  private apiKey: string;
  private model: string;
  private temperature: number | undefined;
  private baseURL = "https://api.groq.com/openai/v1";

  constructor(init: ProviderInit) {
    if (!init.apiKey) {
      throw MetisError.apiKeyMissing('groq');
    }
    
    this.apiKey = init.apiKey;
    this.model = init.model;
    this.temperature = init.temperature;
  }

  async send(messages: Message[], opts?: { temperature?: number }): Promise<string> {
    const temperature = opts?.temperature ?? this.temperature;
    
    try {
      const response = await this.makeRequest('/chat/completions', {
        model: this.model,
        temperature,
        messages: messages.map((m) => ({
          role: m.role,
          content: m.content,
          tool_calls: m.tool_calls,
          tool_call_id: m.tool_call_id,
          name: m.name,
        })),
      });

      const choice = response.choices?.[0]?.message?.content ?? "";
      return typeof choice === "string" ? choice : JSON.stringify(choice);
    } catch (error: any) {
      if (error.status) {
        throw MetisError.providerRequestFailed('groq', error.status);
      }
      throw MetisError.providerRequestFailed('groq');
    }
  }

  async sendWithTools(
    messages: Message[], 
    tools: FunctionDefinition[], 
    opts?: { temperature?: number; max_tokens?: number }
  ): Promise<ProviderResponse> {
    const temperature = opts?.temperature ?? this.temperature;
    
    try {
      const requestConfig: any = {
        model: this.model,
        temperature,
        max_tokens: opts?.max_tokens,
        messages: messages.map((m) => ({
          role: m.role,
          content: m.content,
          tool_calls: m.tool_calls,
          tool_call_id: m.tool_call_id,
          name: m.name,
        })),
        tools: tools.map(tool => ({
          type: "function" as const,
          function: {
            name: tool.name,
            description: tool.description,
            parameters: tool.parameters,
          }
        })),
        tool_choice: "auto",
      };

      // Add Groq-specific parameters for supported models
      if (this.supportsAdvancedFeatures()) {
        requestConfig.service_tier = "on_demand"; // Default to on_demand tier
      }

      const response = await this.makeRequest('/chat/completions', requestConfig);

      const message = response.choices?.[0]?.message;
      if (!message) {
        throw MetisError.providerRequestFailed('groq');
      }

      // Check if the model wants to call tools
      if (message.tool_calls && message.tool_calls.length > 0) {
        return {
          type: "tool_call",
          content: message.content || "",
          tool_calls: message.tool_calls.map(tc => ({
            id: tc.id,
            type: "function",
            function: {
              name: tc.function.name,
              arguments: tc.function.arguments,
            }
          })),
          usage: response.usage ? {
            prompt_tokens: response.usage.prompt_tokens,
            completion_tokens: response.usage.completion_tokens,
            total_tokens: response.usage.total_tokens,
          } : undefined
        };
      }

      // Regular text response
      return {
        type: "text",
        content: message.content || "",
        usage: response.usage ? {
          prompt_tokens: response.usage.prompt_tokens,
          completion_tokens: response.usage.completion_tokens,
          total_tokens: response.usage.total_tokens,
        } : undefined
      };
    } catch (error: any) {
      if (error.status) {
        throw MetisError.providerRequestFailed('groq', error.status);
      }
      throw MetisError.providerRequestFailed('groq');
    }
  }

  supportsTools(): boolean {
    // Current Groq models that support function calling (as of 2025)
    const toolSupportedModels = [
      'llama-3.1-70b-versatile',
      'llama-3.1-8b-instant', 
      'llama-3.3-70b-versatile',
      'mixtral-8x7b-32768',
      'gemma2-9b-it',
      'gemma-7b-it',
    ];
    
    return toolSupportedModels.some(model => this.model.includes(model)) ||
           this.model.includes('tool-use') ||
           this.model.includes('function');
  }

  private supportsAdvancedFeatures(): boolean {
    // Check if model supports advanced Groq features
    return this.model.includes('llama-4') || 
           this.model.includes('mixtral') ||
           this.model.includes('gemma2');
  }

  private async makeRequest(endpoint: string, body: any): Promise<GroqResponse> {
    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}`;
        
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error?.message || errorMessage;
        } catch {
          // Use default error message
        }

        throw { status: response.status, message: errorMessage };
      }

      return await response.json();
    } catch (error: any) {
      if (error.status) {
        throw error;
      }
      throw { message: error.message || 'Network error' };
    }
  }

  // Helper method to get available models
  static getAvailableModels(): string[] {
    return [
      // Llama models
      'llama3-groq-70b-8192-tool-use-preview',
      'llama3-groq-8b-8192-tool-use-preview', 
      'meta-llama/llama-4-scout-17b-16e-instruct',
      'llama-3.3-70b-versatile',
      'llama-3.1-70b-versatile',
      'llama-3.1-8b-instant',
      
      // Mixtral models
      'mixtral-8x7b-32768',
      
      // Gemma models
      'gemma2-9b-it',
      'gemma-7b-it',
      
      // Qwen models
      'qwen2.5-72b-instruct',
      
      // DeepSeek models
      'deepseek-r1-distill-llama-70b',
    ];
  }
}